import { Flex, Checkbox, Button, Link } from '@chakra-ui/react';
import { ShowSkipStop } from '../../components/ShowSkipStop';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRestartOutreachCandidatesMutation } from '@/store/query/mco-candidates';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';
import { CONNECTION_RESTART_COPY } from './messages';

export const ConnectionAlreadyConnected = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-already-connected"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Already part of your network
      </KText>
      <KText type="muted" color="secondary-700">
        {`You are already connected to the candidate. ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'You can either skip ahead to the next step or decide to stop the flow for this candidate'
            : 'You can choose to stop the flow'
        }`}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionAlreadyPending = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const [isAcknowledged, setAcknowledge] = useState(false);
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 40320,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {
      /* noop */
    }
  };
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-already-pending"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        Linkedin - Connection request - Pending
      </KText>
      <KText type="muted" color="secondary-700">
        {CONNECTION_RESTART_COPY.alreadyPending}
      </KText>
      <Flex alignItems="start">
        <Checkbox
          mr="12px"
          isChecked={isAcknowledged}
          onChange={() => setAcknowledge((prev) => !prev)}
          data-test-id="flow-restart-action-connection-already-pending-checkbox"
        >
          I acknowledge that I have withdrawn the previous connection request
        </Checkbox>
      </Flex>
      <Button
        disabled={!isAcknowledged}
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-connection-already-pending-cta"
      >
        Restart After 4 weeks
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionMaxPendingLimitReached = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const [isAcknowledged, setAcknowledge] = useState(false);
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 0,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {
      /* noop */
    }
  };
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-pending-limited"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Maximum pending connection request limit reached
      </KText>
      <KText type="muted" color="secondary-700">
        {CONNECTION_RESTART_COPY.maxPendingLimitPrefix}
        <Link
          href="https://www.linkedin.com/mynetwork/invitation-manager/sent/"
          target="_blank"
          mx="4px"
          color="primary"
        >
          visit this page
        </Link>
        {CONNECTION_RESTART_COPY.maxPendingLimitSuffix}
      </KText>
      <Flex alignItems="start">
        <Checkbox
          mr="12px"
          isChecked={isAcknowledged}
          onChange={() => setAcknowledge((prev) => !prev)}
          data-test-id="flow-restart-action-connection-pending-limited-checkbox"
        >
          <KText>
            I acknowledge that I have withdrawn some connection requests
          </KText>
        </Checkbox>
      </Flex>

      <Button
        disabled={!isAcknowledged}
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-connection-pending-limited-cta"
      >
        Restart Flow
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionRateLimit = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 1440,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {
      /* noop */
    }
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-rate-limited"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Exceeded connection request limits
      </KText>
      <KText type="muted" color="secondary-700">
        You have reached the maximum number of connection request which you can
        send per day. You can choose to perform the below actions
      </KText>
      <Button
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-connection-rate-limited-cta"
      >
        Restart after 1 day
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionProfileNotFound = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-not-found"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn : Profile URL not valid/found
      </KText>
      <KText type="muted" color="secondary-700">
        {`LinkedIn profile URL is not valid/found, preventing us from completing the current step. ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'You can either skip ahead to the next step or decide to stop the flow for this candidate'
            : 'You can choose to stop the flow'
        }`}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionAlreadyRejected = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-already-rejected"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Already rejected your connection request
      </KText>
      <KText type="muted" color="secondary-700">
        {CONNECTION_RESTART_COPY.alreadyRejected}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const LiRetriableError = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 0,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          status: 'error',
        });
        return;
      }
      toast({ title: 'Flow re-started successfully.' });
    } catch {
      /* noop */
    }
  };

  return (
    <Flex flexDir="column" gap="12px">
      <KText size="lg-semi-bold" color="secondary-900">
        Something went wrong
      </KText>
      <KText type="muted" color="secondary-700">
        We ran into an unexpected error. You can try this step again, skip it or
        stop the Flow.
      </KText>
      <Button onClick={onRestart} isLoading={isLoading}>
        Restart
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const AlreadyPresentInATS = () => {
  // const { stage } = restartDetails?.outreach_candidate || {};
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-already-rejected"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        Candidate is already part of linked job
      </KText>
      <KText type="muted" color="secondary-900">
        {/* {stage === 'paused' ? 'The flow is paused' : 'We won’t start flow'}{' '} */}
        Unable to start the Flow for this candidate because they are already
        active in the linked job
      </KText>
    </Flex>
  );
};

export const ConnectionEmailRequired = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-email-required"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Connection request - Email id required.
      </KText>
      <KText type="muted" color="secondary-900">
        {CONNECTION_RESTART_COPY.emailRequired}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionToSelf = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-to-self"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Connection request to self
      </KText>
      <KText type="muted" color="secondary-700">
        {CONNECTION_RESTART_COPY.toSelf}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionLimitReached = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-limit-reached"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn - Exceeded connection request limits
      </KText>
      <KText type="muted" color="secondary-700">
        You have reached the maximum number of connections(30,000). You can
        choose to perform the below actions
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const ConnectionGeneralError = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-connection-error"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        LinkedIn Error - Connection request
      </KText>
      <KText type="muted" color="secondary-700">
        {`LinkedIn has thrown an unexpected error, preventing us from completing
        the current step. ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'You can either skip ahead to the next step or decide to stop the flow for this candidate'
            : 'You can choose to stop the flow'
        }`}
      </KText>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};
